import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap, Gift, TrendingUp, TrendingDown } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { useStorage } from '@/hooks/useStorage';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import type { Reward } from '@/lib/index';

interface PointsLogEntry {
  date: string;
  delta: number;
  reason: string;
  total: number;
}

const POINTS_LOG_KEY = 'yuyu_points_log';

export default function Rewards() {
  const { storage, getChildPoints, addPoints } = useStorage();
  const [pointsLog, setPointsLog] = useState<PointsLogEntry[]>([]);
  const [currentPoints, setCurrentPoints] = useState(0);

  useEffect(() => {
    setCurrentPoints(getChildPoints());

    const storedLog = localStorage.getItem(POINTS_LOG_KEY);
    if (storedLog) {
      try {
        const parsed = JSON.parse(storedLog);
        setPointsLog(Array.isArray(parsed) ? parsed : []);
      } catch {
        setPointsLog([]);
      }
    }
  }, [getChildPoints]);

  const handleRedeem = (reward: Reward) => {
    if (currentPoints < reward.pointsCost) {
      toast.error(`积分不足！还需要 ${reward.pointsCost - currentPoints} ⚡`);
      return;
    }

    addPoints(-reward.pointsCost, `兑换奖励: ${reward.name}`);
    setCurrentPoints((prev) => Math.max(0, prev - reward.pointsCost));
    toast.success(`🎉 成功兑换「${reward.emoji} ${reward.name}」！`);

    const storedLog = localStorage.getItem(POINTS_LOG_KEY);
    if (storedLog) {
      try {
        const parsed = JSON.parse(storedLog);
        setPointsLog(Array.isArray(parsed) ? parsed : []);
      } catch {
        setPointsLog([]);
      }
    }
  };

  const rewards: Reward[] = storage.rewards || [];

  return (
    <Layout>
      <div className="min-h-screen bg-background py-8 pb-28 md:pb-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-2 text-foreground">
              ⚡ 积分奖励
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              累积积分，兑换你想要的奖励！
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-lang-zh-bg to-secondary border-2 border-border rounded-3xl p-6 md:p-8 text-center shadow-lg mb-8"
          >
            <div className="text-6xl md:text-8xl font-bold text-primary mb-2 flex items-center justify-center gap-3">
              <Zap className="w-12 h-12 md:w-16 md:h-16 text-accent" />
              {currentPoints}
            </div>
            <p className="text-xl md:text-2xl text-primary font-semibold">当前积分</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="border-2 border-border rounded-3xl p-6 shadow-sm h-full">
                <h2 className="text-2xl font-heading font-bold mb-4 text-foreground flex items-center gap-2">
                  <Gift className="w-6 h-6 text-primary" />
                  奖励商店
                </h2>

                {rewards.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Gift className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">还没有奖励，请家长在家长后台添加！</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {rewards.map((reward, i) => (
                      <motion.div
                        key={reward.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="bg-secondary border-2 border-border rounded-2xl p-4 flex items-center gap-4"
                      >
                        <div className="text-4xl md:text-5xl flex-shrink-0">{reward.emoji}</div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base md:text-lg font-bold text-foreground truncate">
                            {reward.name}
                          </h3>
                          <div className="text-lg md:text-xl font-bold text-primary mt-1">
                            {reward.pointsCost} ⚡
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleRedeem(reward)}
                          disabled={currentPoints < reward.pointsCost}
                          className="rounded-xl flex-shrink-0"
                        >
                          {currentPoints >= reward.pointsCost
                            ? '兑换'
                            : `差${reward.pointsCost - currentPoints}⚡`}
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="border-2 border-border rounded-3xl p-6 shadow-sm h-full">
                <h2 className="text-2xl font-heading font-bold mb-4 text-foreground">📜 积分记录</h2>
                {pointsLog.length === 0 ? (
                  <p className="text-muted-foreground text-center py-12">还没有积分记录</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {pointsLog.slice(0, 20).map((log, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-3 rounded-xl bg-secondary border border-border"
                      >
                        <div className="flex items-center gap-2">
                          {log.delta > 0 ? (
                            <TrendingUp className="w-4 h-4 text-primary" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-destructive" />
                          )}
                          <span className="text-sm text-foreground">{log.reason}</span>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className={`text-sm font-bold ${log.delta > 0 ? 'text-primary' : 'text-destructive'}`}>
                            {log.delta > 0 ? '+' : ''}{log.delta}⚡
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(log.date).toLocaleDateString('zh-CN')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
